import { makeAutoObservable } from "mobx";
import Parse from "parse";
import {
  AudioDonation,
  AudioDonationAttributes,
  Category,
  CategoryAttributes,
  IndustryAttributes,
  Question,
  QuestionAttributes,
  RegionAttributes,
  _UserAttributes,
} from "../parse";

import type { AppService } from "./AppService";

export class AdminService {
  app: AppService;

  users: _UserAttributes[] = [];
  audioDonations: AudioDonationAttributes[] = [];

  constructor(app: AppService) {
    makeAutoObservable(this);

    this.app = app;
  }

  public get isAdmin() {
    return !!this.app.parse.user?.isAdmin;
  }

  public async init(cache: any = {}) {
    Object.assign(this, cache);

    if (!this.isAdmin) {
      return;
    }

    this.users = (
      await this.app.parse.get("/classes/_User?order=name")
    ).results;

    this.audioDonations = (
      await this.app.parse.get("/classes/AudioDonation?order=-createdAt")
    ).results;
  }

  private updateHasChanges(a: any, b: any) {
    for (const [key, value] of Object.entries(b)) {
      if (a[key] !== value) {
        return true;
      }
    }

    return false;
  }

  public async updateUser(
    id: string | undefined,
    data: Partial<_UserAttributes>,
  ) {
    if (!id) {
      return;
    }
    const user = this.users.find((u) => u.id === id);

    if (!user || !this.updateHasChanges(user, data)) {
      return;
    }

    if ("password" in data && !data.password) {
      return;
    }

    await this.app.parse.put(`/classes/_User/${id}`, data);

    Object.assign(user, await this.app.parse.get(`/classes/_User/${id}`));

    // this.app.notifications.success("Wert wurde geändert");
  }

  public async updateQuestion(
    id: string | undefined,
    data: Partial<QuestionAttributes>,
  ) {
    if (!id) {
      return;
    }
    const question = this.app.benchmarks.questions.find(
      (question) => question.id === id,
    );

    if (!question || !this.updateHasChanges(question, data)) {
      return;
    }

    await this.app.parse.put(`/classes/Question/${id}`, data);

    Object.assign(question, data);

    // this.app.notifications.success("Wert wurde geändert");
  }

  public async updateCategory(
    id: string | undefined,
    data: Partial<CategoryAttributes>,
  ) {
    if (!id) {
      return;
    }
    const category = this.app.benchmarks.categories.find(
      (category) => category.id === id,
    );

    if (!category || !this.updateHasChanges(category, data)) {
      return;
    }

    await this.app.parse.put(`/classes/Category/${id}`, data);

    Object.assign(category, data);

    // this.app.notifications.success("Wert wurde geändert");
  }

  public async updateAudioDonation(
    id: string,
    data: Partial<AudioDonationAttributes>,
  ) {
    const audioDonation = this.audioDonations.find(
      (donation) => donation.id === id,
    );

    if (!audioDonation || !this.updateHasChanges(audioDonation, data)) {
      return;
    }

    await this.app.parse.put(`/classes/AudioDonation/${id}`, data);

    Object.assign(audioDonation, data);

    // this.app.notifications.success("Wert wurde geändert");
  }

  public async addToDataset(id: string) {
    await this.updateAudioDonation(id, { dataset: true });
  }

  public async removeFromDataset(id: string) {
    await this.updateAudioDonation(id, { dataset: false });
  }

  public async downloadDataset() {
    const response = await this.app.parse.cc("export-dataset");
    console.log(response);

    const link = document.createElement("a");
    link.href = response.result;
    link.download = "dataset.zip";
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  public async addToGame(id: string) {
    const audioDonation = this.audioDonations.find(
      (donation) => donation.id === id,
    );

    if (!audioDonation || !audioDonation.audio) {
      return;
    }

    // Create a new Question object
    const defaultCategory = await new Parse.Query(Category.className)
      .ascending("createdAt")
      .first();

    if (!defaultCategory) {
      throw new Error("No default category found");
    }

    const question = new Parse.Object(Question.className);
    question.set("name", audioDonation.spokenText || "Audio Donation Question");
    question.set("audio", audioDonation.audio);
    question.set("synthetic", audioDonation.synthetic || false);
    if (defaultCategory) {
      question.set("category", defaultCategory);
    }

    // await this.app.parse.post(`/classes/Question`, {
    //   name: audioDonation.spokenText || "Audio Donation Question",
    //   audio: audioDonation.audio,
    //   synthetic: audioDonation.synthetic,
    //   category: new Parse.Object("Category", {
    //     objectId: defaultCategory?.id,
    //   }),
    // });

    await question.save();

    // Mark the audio donation as added to game
    await this.updateAudioDonation(id, { game: true });

    // Refresh questions data
    await this.app.benchmarks.init({});
  }

  public async updateIndustry(
    id: string | undefined,
    key: keyof IndustryAttributes,
    value: any,
  ) {
    if (!id) {
      return;
    }
    const data = {
      [key]: value,
    };

    const industry = this.app.benchmarks.industries.find(
      (industry) => industry.id === id,
    );

    if (!industry || industry[key] === value) {
      return;
    }

    await this.app.parse.put(`/classes/Industry/${id}`, data);

    Object.assign(industry, data);

    // this.app.notifications.success("Wert wurde geändert");
  }

  public async updateRegion(
    id: string | undefined,
    key: keyof RegionAttributes,
    value: any,
  ) {
    if (!id) {
      return;
    }
    const data = {
      [key]: value,
    };

    const region = this.app.benchmarks.regions.find(
      (region) => region.id === id,
    );

    if (!region || region[key] === value) {
      return;
    }

    await this.app.parse.put(`/classes/Region/${id}`, data);

    Object.assign(region, data);

    // this.app.notifications.success("Wert wurde geändert");
  }

  public async create(className: string) {
    if (className === "_User") {
      return this.createUser();
    }

    const category = Parse.Object.extend(Category.className);
    category.id = "mFIKmJZSNA";

    const response = await this.app.parse.post(`/classes/${className}`, {
      name: "Neues Element",
      category: category,
      audio: undefined,
    });

    await this.app.benchmarks.init({});

    // this.app.notifications.success("Element wurde erstellt");

    return response.id;
  }

  public async createUser() {
    const response = await this.app.parse.post(`/classes/_User`, {
      name: "Neuer Benutzer",
      username: "-",
      password: Math.random().toString(),
    });

    await this.init();

    // this.app.notifications.success("Element wurde erstellt");

    return response.id;
  }

  public async delete(className: string, id: string) {
    if (window.confirm("Sicher dass das Element gelöscht werden soll?")) {
      const response = await this.app.parse.delete(
        `/classes/${className}/${id}`,
      );

      if (className === "_User" || className === AudioDonation.className) {
        await this.init();
      } else {
        await this.app.benchmarks.init({});
      }

      // this.app.notifications.success("Element wurde gelöscht");
    }
  }

  public async downloadContacts() {
    const response = await this.app.parse.cc("export-contacts");

    // this.app.notifications.success("Kontakte werden heruntergeladen");

    this.exportToCsv("benchmarks.csv", response.result);
  }

  public async downloadBenchmarks() {
    const response = await this.app.parse.cc("export-benchmarks");

    // this.app.notifications.success("Benchmarks werden heruntergeladen");

    this.exportToCsv("benchmarks.csv", response.result);
  }

  private async exportToCsv(filename: string, rows: any[][]) {
    const csvFile = rows
      .map((row) =>
        row
          .map((col) =>
            col == null
              ? ""
              : col instanceof Date
              ? col.toLocaleString()
              : col.toString(),
          )
          .map((col) => col.replace(/"/g, '""'))
          .map((col) => (col.search(/("|;|\n)/g) >= 0 ? '"' + col + '"' : col))
          .join(";"),
      )
      .join("\n");

    var blob = new Blob(["\ufeff" + csvFile], {
      type: "text/csv;charset=utf-8;",
    });
    // if (navigator.msSaveBlob) {
    //   // IE 10+
    //   navigator.msSaveBlob(blob, filename);
    // } else {
    const link = document.createElement("a");
    if (link.download !== undefined) {
      // feature detection
      // Browsers that support HTML5 download attribute
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    // }
  }
}
